import React from "react";
import Highcharts from "highcharts";
import HighchartsReact from "highcharts-react-official";
import { FaIndustry } from "react-icons/fa";
import utahWaterData from "../data/utahWaterData";

const brand = {
  primary: "#0F8ECF",
  secondary: "#0FA892",
  accent: "#F2A93B",
  ink: "#143A54",
  muted: "#51738B",
};

const sectionTheme = {
  border: "#C4E0F1",
};

const sectorPalette = {
  Agriculture: "#4E8B31",
  "Municipal & Industrial": "#0B5D7A",
};

const miSubsectorPalette = {
  Residential: "#1696A3",
  Commercial: "#D98A2F",
  Institutional: "#6B7280",
  Industrial: "#B85C38",
  "Outdoor Residential": "#12A39A",
  "Indoor Residential": "#56A3D9",
};

const formatPercent = (value) => {
  if (typeof value !== "number") return value;
  return `${Number(value.toFixed(2)).toString()}%`;
};

const formatPieLabel = (point) =>
  `${point.name}<br /><b>${formatPercent(point.y)}</b>`;

const sectorBreakdownChartData = utahWaterData.sectorBreakdown.map((item) => ({
  ...item,
  label: item.sector,
  color: sectorPalette[item.sector] || brand.primary,
}));

const municipalIndustrialBreakdown = utahWaterData.sectorBreakdown.find(
  (item) => item.sector === "Municipal & Industrial"
);

const municipalIndustrialSubsectorData = utahWaterData.municipalIndustrialBreakdown.map(
  (item) => ({
    ...item,
    color: miSubsectorPalette[item.label] || brand.primary,
    totalDiversionShare:
      item.estimatedTotalStateShare ??
      Number(
        (
          ((municipalIndustrialBreakdown?.share || 0) * item.shareValue) /
          100
        ).toFixed(2)
      ),
  })
);

const residentialMunicipalShare = municipalIndustrialSubsectorData.find(
  (item) => item.label === "Residential"
);

const municipalResidentialChartData = utahWaterData.municipalResidentialBreakdown.map(
  (item) => ({
    ...item,
    color: miSubsectorPalette[item.label] || brand.primary,
    totalDiversionShare:
      item.estimatedTotalStateShare ??
      Number(
        (
          ((municipalIndustrialBreakdown?.share || 0) *
            (residentialMunicipalShare?.shareValue || 0) *
            item.shareValue) /
          10000
        ).toFixed(2)
      ),
  })
);

const municipalIndustrialNonResidentialData = municipalIndustrialSubsectorData.filter(
  (item) => item.label !== "Residential"
);

const combinedWater3DChartData = [
  {
    name: "Agriculture",
    y:
      sectorBreakdownChartData.find((item) => item.label === "Agriculture")
        ?.share || 75,
    color: sectorPalette.Agriculture,
    detail: "Crop irrigation and livestock",
    level: "Total diversion",
  },
  ...municipalResidentialChartData.map((item) => ({
    name: item.label,
    y: item.totalDiversionShare,
    color: item.color,
    detail: item.description,
    level: "M&I share of total diversion",
  })),
  ...municipalIndustrialNonResidentialData.map((item) => ({
    name: item.label,
    y: item.totalDiversionShare,
    color: item.color,
    detail: item.detail,
    level: "M&I share of total diversion",
  })),
];

const parentWater3DChartData = sectorBreakdownChartData.map((item) => ({
  name: item.label === "Municipal & Industrial" ? "M&I" : item.label,
  fullName: item.label,
  y: item.share,
  color: item.color,
  description: item.useCase,
}));

const mergedNestedWaterChartOptions = {
  chart: {
    type: "pie",
    backgroundColor: "transparent",
    spacing: [10, 10, 10, 10],
  },
  title: {
    text: "",
    align: "left",
    style: {
      color: brand.ink,
      fontSize: "18px",
      fontWeight: "700",
    },
  },
  subtitle: {
    text: "Breakdown of Utah's water use by sector and subsector",
    align: "left",
    style: {
      color: brand.muted,
      fontSize: "12px",
    },
  },
  tooltip: {
    useHTML: true,
    backgroundColor: "rgba(255,255,255,0.96)",
    borderColor: sectionTheme.border,
    borderRadius: 18,
    shadow: false,
    formatter: function () {
      return `
        <div style="min-width:210px">
          <div style="font-weight:700;color:${brand.ink};margin-bottom:8px;">${this.point.fullName || this.point.name}</div>
          <div style="color:${brand.muted};margin-bottom:4px;">Share shown: <b style="color:${brand.ink};">${formatPercent(this.y)}</b></div>
          <div style="color:${brand.muted};margin-bottom:4px;">Layer: <b style="color:${brand.ink};">${this.point.layer || "Overview"}</b></div>
          <div style="color:${brand.muted};">${this.point.detail || this.point.description || ""}</div>
        </div>
      `;
    },
  },
  plotOptions: {
    pie: {
      allowPointSelect: false,
      cursor: "pointer",
      borderWidth: 2,
      borderColor: "#ffffff",
      dataLabels: {
        enabled: true,
        style: {
          color: brand.ink,
          fontSize: "11px",
          fontWeight: "600",
          textOutline: "none",
        },
      },
      showInLegend: false,
      startAngle: -90,
      endAngle: 270,
    },
  },
  series: [
    {
      type: "pie",
      name: "Parent split",
      size: "58%",
      innerSize: "28%",
      dataLabels: {
        enabled: true,
        distance: -22,
        useHTML: true,
        formatter: function () {
          return formatPieLabel(this.point);
        },
        style: {
          color: "#ffffff",
          fontSize: "11px",
          fontWeight: "700",
          textOutline: "none",
        },
      },
      data: parentWater3DChartData.map((item) => ({
        ...item,
        layer: "Parent split",
      })),
    },
    {
      type: "pie",
      name: "Detailed split",
      size: "92%",
      innerSize: "66%",
      dataLabels: {
        enabled: true,
        distance: 16,
        useHTML: true,
        softConnector: true,
        connectorColor: "#8fb6ce",
        formatter: function () {
          return formatPieLabel(this.point);
        },
      },
      data: combinedWater3DChartData.map((item) => ({
        ...item,
        layer:
          item.name === "Agriculture"
            ? "Outer detailed ring"
            : "M&I detailed ring",
      })),
    },
  ],
  credits: {
    enabled: false,
  },
};

export default function WaterUseBreakdownChart({
  cardClassName = "",
  compact = false,
}) {
  return (
    <div
      className={cardClassName}
      style={{
        border: "1px solid rgba(196, 224, 241, 0.9)",
        borderRadius: 24,
        padding: compact ? 18 : 20,
        background:
          "linear-gradient(180deg, rgba(255,255,255,0.96) 0%, rgba(232, 247, 253, 0.86) 55%, rgba(244, 250, 232, 0.68) 100%)",
        boxShadow: "0 18px 34px rgba(15, 142, 207, 0.10)",
      }}
    >
      <div className="flex flex-col gap-3 lg:flex-row lg:items-end lg:justify-between">
        <div>
          <h3 style={{ color: brand.ink }}>
            <FaIndustry style={{ marginRight: 8 }} /> Water use breakdown by sector
          </h3>
          </div>
       
      </div>

      

      <div
        className="mt-4 rounded-[30px] border p-3 sm:p-4"
        style={{
          borderColor: sectionTheme.border,
          background:
            "linear-gradient(180deg, rgba(255,255,255,0.92) 0%, rgba(232, 247, 253, 0.72) 100%)",
        }}
      >
        <div className={compact ? "min-h-[460px]" : "min-h-[520px]"}>
          <HighchartsReact
            highcharts={Highcharts}
            options={mergedNestedWaterChartOptions}
          />
        </div>
      </div>

      <div className="mt-3 flex flex-wrap gap-3">
        {combinedWater3DChartData.map((item) => (
          <span
            key={item.name}
            className="rounded-full px-3 py-2 text-[11px] font-semibold uppercase tracking-[0.14em]"
            style={{
              background: `${item.color}16`,
              color: item.color,
            }}
          >
            {item.name}: {formatPercent(item.y)}
          </span>
        ))}
      </div>

      <div className="mt-3 flex flex-wrap gap-2">
        {parentWater3DChartData.map((item) => (
          <span
            key={`parent-chart-chip-${item.name}`}
            className="rounded-full px-3 py-2 text-[11px] font-semibold uppercase tracking-[0.14em]"
            style={{
              background: `${item.color}16`,
              color: item.color,
            }}
          >
            {item.name === "M&I"
              ? `M&I (Municipal & Industrial): ${formatPercent(item.y)}`
              : `${item.name}: ${formatPercent(item.y)}`}
          </span>
        ))}
      </div>
    </div>
  );
}
